import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Nav, Spinner, Table,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import useModalState from '../../../Hooks/useModalState';
import UserService from '../../../Services/UserService';
import Permissions from '../../../Utils/Permissions';
import KnowledgeBaseService, {
  AUDIENCES, AUDIENCE_LABELS,
} from '../../../Services/KnowledgeBaseService';
import KnowledgeBaseInputModal from '../Components/KnowledgeBaseInputModal';
import RetrievalPreview from '../Components/RetrievalPreview';
import KnowledgeBaseAsk from '../Components/KnowledgeBaseAsk';
import KnowledgeBaseUploader from '../../../Components/KnowledgeBase/KnowledgeBaseUploader';
import KnowledgeBaseDocuments from '../../../Components/KnowledgeBase/KnowledgeBaseDocuments';
import { formatDateTime } from '../../../Utils/timezone';

/**
 * The Knowledge Bases area of AI Admin: manage the knowledge bases behind
 * the assistant's document retrieval, and — the point of the screen —
 * TEST them.
 *
 * This is an AREA, registered in ../areas.js and rendered inside the
 * AI Admin shell (../index.jsx) at /ai-admin/knowledge-bases. It owns its
 * own heading and actions; the shell owns the area selector above it. A new
 * AI area is a sibling of this file plus one entry in ../areas.js.
 *
 * Two test tools, side by side as tabs, because they answer different
 * questions and either one alone is misleading:
 *
 *   Retrieval  "did my document get indexed, and what comes back?"  No model.
 *   Ask        "what would an operator actually be told?"            Real chat.
 *
 * A good-looking answer over an empty retrieval is the failure this pairing
 * exists to catch, so neither tab is the "advanced" one.
 *
 * PERMISSION: `ManageAi` gates MANAGING — create, delete, upload,
 * re-ingest. It does not gate asking: the route and the nav entry carry the
 * module gate (Routes.jsx, config/navigation.js) the same way Payments and
 * Month End do, and within the screen the managing affordances check it
 * again so a stale module list cannot leave a delete button live.
 */

const INGESTION_VARIANTS = {
  COMPLETE: 'success',
  IN_PROGRESS: 'info',
  STARTING: 'info',
  FAILED: 'danger',
  STOPPED: 'warning',
};

/** Bedrock's job statuses are SCREAMING_SNAKE; read them as words. */
export function ingestionLabel(status) {
  if (!status) return 'Never ingested';
  return String(status)
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

const TABS = {
  DOCUMENTS: 'documents',
  RETRIEVAL: 'retrieval',
  ASK: 'ask',
};

function KnowledgeBasesArea() {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [tab, setTab] = useState(TABS.DOCUMENTS);
  const [reloadToken, setReloadToken] = useState(0);
  const [reingesting, setReingesting] = useState('');
  const [jobNotice, setJobNotice] = useState(null);

  const inputModal = useModalState();

  const canManage = UserService.getInstance()
    .canAccessModule(Permissions.MANAGE_AI);

  const fetchKnowledgeBases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await KnowledgeBaseService.list();
      const list = Array.isArray(data) ? data : [];
      setKnowledgeBases(list);
      // Keep the operator's selection across a refetch; fall back to the
      // first base only when what they had selected is gone.
      setSelectedId((prev) => (
        list.some((kb) => kb.id === prev) ? prev : (list[0]?.id || '')
      ));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load knowledge bases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  const selected = knowledgeBases.find((kb) => kb.id === selectedId) || null;

  const handleDeleteKb = async (kb) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Delete the knowledge base "${kb.name}" and everything in it? This cannot be undone.`,
    )) return;
    setError('');
    try {
      await KnowledgeBaseService.remove(kb.id);
      await fetchKnowledgeBases();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete the knowledge base');
    }
  };

  const handleReingest = async (kb) => {
    setReingesting(kb.id);
    setError('');
    setJobNotice(null);
    try {
      const job = await KnowledgeBaseService.reingest(kb.id);
      setJobNotice({
        kbId: kb.id,
        kbName: kb.name,
        jobId: job?.job_id || '',
        status: job?.status || 'STARTING',
      });
      // The list carries `ingestion_status`, so refetching is how the row
      // catches up with the job that was just started.
      await fetchKnowledgeBases();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to start re-ingestion');
    } finally {
      setReingesting('');
    }
  };

  return (
    <>
      {/* No <h1> and no hero: the AI Admin shell renders the page's heading
          from this area's own label in ../areas.js. One heading per page,
          one copy of the words. */}
      <div className="ai-admin-area-head">
        <p className="ai-admin-area-intro">
          The documents the assistant answers operational questions from —
          and the two tools for checking that it does.
        </p>
        {canManage && (
          <Button onClick={() => inputModal.show({})} variant="primary">
            <i className="fa fa-plus me-2" aria-hidden="true" />
            New Knowledge Base
          </Button>
        )}
      </div>

      <Error error={error} setError={setError} />

      {jobNotice && (
        <Alert variant="info" dismissible onClose={() => setJobNotice(null)}>
          <strong>{`Re-ingestion started for “${jobNotice.kbName}”.`}</strong>
          {jobNotice.jobId && (
            <>
              {' Job '}
              <code>{jobNotice.jobId}</code>
            </>
          )}
          {` · ${ingestionLabel(jobNotice.status)}.`}
          {' Refresh the list to follow it.'}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading knowledge bases…</p>
        </div>
      ) : (
        <>
          <Table
            responsive
            striped
            bordered
            hover
            className="responsive-card-table"
            aria-label="Knowledge bases"
          >
            <thead>
              <tr>
                <th>Name</th>
                <th>Audience</th>
                <th>Documents</th>
                <th>Last ingestion</th>
                <th>Status</th>
                <th style={{ width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {knowledgeBases.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    <i className="fa fa-book fa-2x mb-2 d-block opacity-50" aria-hidden="true" />
                    {canManage
                      ? 'No knowledge bases yet. Create one, then upload the documents it should answer from.'
                      : 'No knowledge bases yet.'}
                  </td>
                </tr>
              ) : knowledgeBases.map((kb) => (
                <tr
                  key={kb.id}
                  className={kb.id === selectedId ? 'table-active' : undefined}
                >
                  <td data-label="Name">
                    <Button
                      variant="link"
                      className="p-0 text-start fw-semibold"
                      onClick={() => setSelectedId(kb.id)}
                    >
                      {kb.name}
                    </Button>
                    {kb.description && (
                      <small className="text-muted d-block">{kb.description}</small>
                    )}
                  </td>
                  <td data-label="Audience">
                    <Badge bg={kb.audience === AUDIENCES.CUSTOMER_SAFE ? 'warning' : 'secondary'}>
                      {AUDIENCE_LABELS[kb.audience] || kb.audience}
                    </Badge>
                  </td>
                  <td data-label="Documents">{kb.document_count ?? 0}</td>
                  <td data-label="Last ingestion">
                    {kb.last_ingested_at ? formatDateTime(kb.last_ingested_at) : '—'}
                  </td>
                  <td data-label="Status">
                    <Badge bg={INGESTION_VARIANTS[kb.ingestion_status] || 'light'} text={INGESTION_VARIANTS[kb.ingestion_status] ? undefined : 'dark'}>
                      {ingestionLabel(kb.ingestion_status)}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant={kb.id === selectedId ? 'primary' : 'outline-primary'}
                        onClick={() => setSelectedId(kb.id)}
                      >
                        <i className="fa fa-flask me-1" aria-hidden="true" />
                        Manage &amp; test
                      </Button>
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={reingesting === kb.id}
                            onClick={() => handleReingest(kb)}
                            title="Re-index everything in this knowledge base"
                          >
                            {reingesting === kb.id
                              ? <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                              : <i className="fa fa-rotate" aria-hidden="true" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteKb(kb)}
                            aria-label={`Delete ${kb.name}`}
                          >
                            <i className="fa fa-trash" aria-hidden="true" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {selected && (
            <Card className="mt-4">
              <Card.Header className="pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <div>
                    <strong>{selected.name}</strong>
                    <span className="text-muted small ms-2">
                      {`${selected.document_count ?? 0} document${(selected.document_count ?? 0) === 1 ? '' : 's'}`}
                      {selected.bedrock_kb_id && ` · ${selected.bedrock_kb_id}`}
                    </span>
                  </div>
                  {selected.s3_prefix && (
                    <code className="small text-muted">{selected.s3_prefix}</code>
                  )}
                </div>
                {/* Tabs, not an accordion: the two test tools answer
                    different questions about the same base and the operator
                    moves between them while debugging one document. */}
                {/* `role="tablist"` is what makes react-bootstrap give each
                    Nav.Link role="tab" rather than role="button" — the tabs
                    announce themselves as tabs to a screen reader. */}
                <Nav
                  variant="tabs"
                  role="tablist"
                  activeKey={tab}
                  onSelect={(k) => setTab(k || TABS.DOCUMENTS)}
                >
                  <Nav.Item>
                    <Nav.Link eventKey={TABS.DOCUMENTS}>
                      <i className="fa fa-folder-open me-2" aria-hidden="true" />
                      <span className="tab-text">Documents</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={TABS.RETRIEVAL}>
                      <i className="fa fa-magnifying-glass me-2" aria-hidden="true" />
                      <span className="tab-text">Retrieval preview</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={TABS.ASK}>
                      <i className="fa fa-comments me-2" aria-hidden="true" />
                      <span className="tab-text">Ask</span>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Header>
              <Card.Body>
                {tab === TABS.DOCUMENTS && (
                  <>
                    {canManage ? (
                      <div className="mb-4">
                        <KnowledgeBaseUploader
                          knowledgeBases={knowledgeBases}
                          kbId={selected.id}
                          onUploaded={() => {
                            setReloadToken((n) => n + 1);
                            fetchKnowledgeBases();
                          }}
                        />
                        <hr />
                      </div>
                    ) : (
                      <Alert variant="light" className="border py-2 px-3 small">
                        <i className="fa fa-lock me-2 text-muted" aria-hidden="true" />
                        Uploading and deleting documents needs the AI Admin
                        permission. Asking questions does not.
                      </Alert>
                    )}
                    <KnowledgeBaseDocuments
                      kbId={selected.id}
                      canManage={canManage}
                      reloadToken={reloadToken}
                    />
                  </>
                )}

                {tab === TABS.RETRIEVAL && <RetrievalPreview knowledgeBase={selected} />}

                {tab === TABS.ASK && <KnowledgeBaseAsk knowledgeBase={selected} />}
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {inputModal.isVisible && (
        <KnowledgeBaseInputModal
          show={inputModal.isVisible}
          handleClose={inputModal.hide}
          onSuccess={fetchKnowledgeBases}
        />
      )}
    </>
  );
}

export default KnowledgeBasesArea;
